import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronDown, ExternalLink, Send } from 'lucide-react';
import toast from 'react-hot-toast';
import Spinner from '../components/ui/Spinner';
import Badge from '../components/ui/Badge';
import Card from '../components/ui/Card';
import { getBugReport, updateBugReportStatus, addMemo } from '../services/bugReportService';
import { formatRelativeTime } from '../utils/dateUtils';

const STATUS_CONFIG: Record<string, { variant: 'error' | 'warning' | 'success'; label: string }> = {
  OPEN: { variant: 'error', label: 'Open' },
  IN_PROGRESS: { variant: 'warning', label: '진행중' },
  RESOLVED: { variant: 'success', label: '해결' },
};

const CATEGORY_CONFIG: Record<string, { variant: 'error' | 'info' | 'default'; label: string }> = {
  BUG: { variant: 'error', label: 'Bug' },
  FEATURE_REQUEST: { variant: 'info', label: '기능 요청' },
  QUESTION: { variant: 'default', label: '질문' },
};

const NEXT_STATUS: Record<string, { status: string; label: string }> = {
  OPEN: { status: 'IN_PROGRESS', label: '처리 시작' },
  IN_PROGRESS: { status: 'RESOLVED', label: '해결 완료' },
  RESOLVED: { status: 'OPEN', label: '재오픈' },
};

export default function BugReportDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: report, isLoading } = useQuery({
    queryKey: ['admin', 'bug-report', id],
    queryFn: () => getBugReport(id!),
    enabled: !!id,
  });

  // --- Status change ---
  const [statusDropdown, setStatusDropdown] = useState(false);
  const [confirmStatus, setConfirmStatus] = useState<string | null>(null);
  const [sendNotification, setSendNotification] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setStatusDropdown(false);
    }
    if (statusDropdown) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [statusDropdown]);

  const statusMutation = useMutation({
    mutationFn: (params: { status: string; sendNotification: boolean }) => updateBugReportStatus(id!, params),
    onSuccess: () => {
      toast.success('상태가 변경되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['admin', 'bug-report', id] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'bug-reports'] });
      setStatusDropdown(false);
      setConfirmStatus(null);
    },
    onError: () => toast.error('상태 변경에 실패했습니다.'),
  });

  // --- Memo ---
  const [memoContent, setMemoContent] = useState('');

  const memoMutation = useMutation({
    mutationFn: (content: string) => addMemo(id!, content),
    onSuccess: () => {
      toast.success('메모가 추가되었습니다.');
      setMemoContent('');
      queryClient.invalidateQueries({ queryKey: ['admin', 'bug-report', id] });
    },
    onError: () => toast.error('메모 추가에 실패했습니다.'),
  });

  if (isLoading) return <div className="flex justify-center py-16"><Spinner className="size-8" /></div>;
  if (!report) return <p className="text-center text-gray-400 py-16">오류 보고를 찾을 수 없습니다.</p>;

  const st = STATUS_CONFIG[report.status] ?? STATUS_CONFIG.OPEN;
  const cat = CATEGORY_CONFIG[report.category] ?? CATEGORY_CONFIG.BUG;
  const next = NEXT_STATUS[report.status];

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div>
        <button onClick={() => navigate('/bug-reports')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-2">
          <ChevronLeft className="size-4" /> 목록으로
        </button>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">#{report.reportNumber} {report.title}</h1>
          </div>
          {/* Status dropdown */}
          <div ref={dropdownRef} className="relative">
            <button onClick={() => setStatusDropdown((v) => !v)}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <Badge variant={st.variant}>{st.label}</Badge>
              <ChevronDown className="size-4 text-gray-400" />
            </button>
            {statusDropdown && next && (
              <div className="absolute right-0 top-full mt-1 w-40 bg-white shadow-lg rounded-lg border border-gray-200 py-1 z-10">
                <button
                  onClick={() => { setConfirmStatus(next.status); setStatusDropdown(false); setSendNotification(true); }}
                  className="w-full px-3 py-2 text-sm text-left text-gray-700 hover:bg-gray-50"
                >
                  {next.label}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 보고자 정보 + 메타 */}
      <Card title="보고자 정보">
        <dl className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
          <div><dt className="text-gray-500">이름</dt><dd className="text-gray-900 font-medium mt-0.5">{report.user?.name ?? '-'}</dd></div>
          <div><dt className="text-gray-500">이메일</dt><dd className="text-gray-900 font-medium mt-0.5">{report.reporterEmail}</dd></div>
          <div><dt className="text-gray-500">소속 조직</dt><dd className="mt-0.5">{report.organization ? (
            <button onClick={() => navigate(`/organizations/${report.organization!.id}`)} className="text-primary-600 hover:underline font-medium">{report.organization.name}</button>
          ) : <span className="text-gray-400">없음</span>}</dd></div>
          <div><dt className="text-gray-500">접수일</dt><dd className="text-gray-900 font-medium mt-0.5">{new Date(report.createdAt).toLocaleString('ko-KR')}</dd></div>
        </dl>
        <div className="flex items-center gap-2 mt-3">
          <Badge variant={cat.variant}>{cat.label}</Badge>
          <Badge variant={st.variant}>{st.label}</Badge>
        </div>
      </Card>

      {/* Description */}
      <Card title="상세 설명">
        <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{report.description}</p>
      </Card>

      {/* Attachments */}
      {(() => {
        const atts = Array.isArray(report.attachments) ? report.attachments as Array<{ url: string; originalName?: string; mimeType?: string; size?: number }> : [];
        if (atts.length === 0) return null;
        return (
          <Card title={`첨부파일 (${atts.length})`}>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {atts.map((att, i) => {
                const isImg = att.mimeType?.startsWith('image/') ?? att.url?.match(/\.(png|jpg|jpeg|gif|webp)$/i);
                return (
                  <a key={i} href={att.url} target="_blank" rel="noopener noreferrer"
                    className="group block border border-gray-200 rounded-lg overflow-hidden hover:border-primary-300 transition-colors">
                    {isImg ? (
                      <img src={att.url} alt={att.originalName ?? ''} className="w-full h-32 object-cover bg-gray-50" />
                    ) : (
                      <div className="w-full h-32 bg-gray-50 flex items-center justify-center">
                        <span className="text-sm text-gray-400">동영상</span>
                      </div>
                    )}
                    <div className="px-2 py-1.5 flex items-center justify-between">
                      <span className="text-sm text-gray-600 truncate">{att.originalName ?? `파일 ${i + 1}`}</span>
                      <ExternalLink className="size-3 text-gray-300 group-hover:text-primary-500 shrink-0" />
                    </div>
                  </a>
                );
              })}
            </div>
          </Card>
        );
      })()}

      {/* Auto-collected info */}
      {(report.pageUrl || report.userAgent) && (() => {
        const ua = report.userAgent ?? '';
        const os = parseOS(ua);
        const browser = parseBrowser(ua);
        return (
          <Card title="자동 수집 정보">
            <div className="space-y-2 text-sm text-gray-500">
              {report.pageUrl && (
                <div className="flex gap-2"><span className="font-medium text-gray-600 w-16 shrink-0">페이지</span><span className="truncate">{report.pageUrl}</span></div>
              )}
              {os && (
                <div className="flex gap-2"><span className="font-medium text-gray-600 w-16 shrink-0">OS</span><span>{os}</span></div>
              )}
              {browser && (
                <div className="flex gap-2"><span className="font-medium text-gray-600 w-16 shrink-0">브라우저</span><span>{browser}</span></div>
              )}
            </div>
          </Card>
        );
      })()}

      {/* Memos */}
      <Card title="운영자 메모">
        {/* Input */}
        <div className="flex gap-2 mb-4">
          <textarea
            value={memoContent}
            onChange={(e) => setMemoContent(e.target.value)}
            placeholder="운영 메모를 입력하세요..."
            rows={2}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none outline-none focus:ring-2 focus:ring-primary-500"
          />
          <button
            onClick={() => memoContent.trim() && memoMutation.mutate(memoContent.trim())}
            disabled={!memoContent.trim() || memoMutation.isPending}
            className="px-3 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700
              disabled:opacity-50 disabled:cursor-not-allowed transition-colors self-end"
          >
            <Send className="size-4" />
          </button>
        </div>

        {/* Memo list */}
        {report.memos.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-2">메모가 없습니다.</p>
        ) : (
          <div className="space-y-3">
            {[...report.memos].reverse().map((memo) => (
              <div key={memo.id} className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-900">{memo.author.name}</span>
                  <span className="text-sm text-gray-400">{formatRelativeTime(memo.createdAt)}</span>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{memo.content}</p>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Status change confirmation dialog */}
      {confirmStatus && report && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 shadow-xl max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">상태 변경 확인</h3>
            <p className="text-sm text-gray-600 mb-1">
              #{report.reportNumber}의 상태를 변경합니다.
            </p>
            <p className="text-sm font-medium text-gray-900 mb-4">
              {STATUS_CONFIG[report.status]?.label} → {STATUS_CONFIG[confirmStatus]?.label ?? confirmStatus}
            </p>

            <label className="flex items-start gap-2 mb-1">
              <input type="checkbox" checked={sendNotification}
                onChange={(e) => setSendNotification(e.target.checked)}
                disabled={!report.reporterEmail && !report.user}
                className="mt-0.5 rounded border-gray-300" />
              <span className="text-sm text-gray-700">보고자에게 이메일 알림 발송</span>
            </label>
            <p className="text-sm text-gray-500 ml-6 mb-4">
              {report.reporterEmail
                ? `(보고자: ${report.reporterEmail})`
                : report.user?.email
                  ? `(보고자: ${report.user.email})`
                  : '(보고자 이메일 없음)'}
            </p>

            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmStatus(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors">
                취소
              </button>
              <button
                onClick={() => statusMutation.mutate({ status: confirmStatus, sendNotification })}
                disabled={statusMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50">
                {statusMutation.isPending ? '변경 중...' : '변경'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function parseOS(ua: string): string {
  if (/Windows NT 10/.test(ua)) return 'Windows 10/11';
  if (/Windows NT/.test(ua)) return 'Windows';
  if (/Mac OS X (\d+[._]\d+)/.test(ua)) {
    const ver = ua.match(/Mac OS X (\d+[._]\d+)/)?.[1]?.replace(/_/g, '.') ?? '';
    return `macOS ${ver}`;
  }
  if (/Mac OS X/.test(ua)) return 'macOS';
  if (/Linux/.test(ua)) return 'Linux';
  if (/Android ([\d.]+)/.test(ua)) return `Android ${ua.match(/Android ([\d.]+)/)?.[1]}`;
  if (/iPhone OS ([\d_]+)/.test(ua)) return `iOS ${ua.match(/iPhone OS ([\d_]+)/)?.[1]?.replace(/_/g, '.')}`;
  if (/iPad/.test(ua)) return 'iPadOS';
  return '';
}

function parseBrowser(ua: string): string {
  if (/Edg\/([\d.]+)/.test(ua)) return `Edge ${ua.match(/Edg\/([\d.]+)/)?.[1]}`;
  if (/OPR\/([\d.]+)/.test(ua)) return `Opera ${ua.match(/OPR\/([\d.]+)/)?.[1]}`;
  if (/Chrome\/([\d.]+)/.test(ua) && !/Edg/.test(ua)) return `Chrome ${ua.match(/Chrome\/([\d.]+)/)?.[1]}`;
  if (/Safari\/([\d.]+)/.test(ua) && !/Chrome/.test(ua)) {
    const ver = ua.match(/Version\/([\d.]+)/)?.[1] ?? '';
    return `Safari ${ver}`;
  }
  if (/Firefox\/([\d.]+)/.test(ua)) return `Firefox ${ua.match(/Firefox\/([\d.]+)/)?.[1]}`;
  return '';
}
